import React, { useState, useEffect } from 'react'
import { FaPlus, FaTrash, FaPaperclip, FaTimes, FaPencilAlt, FaRegCopy } from 'react-icons/fa'
import { Box, Stack, ResponsiveStack, Alert, Heading, Text, Icon, Button, Loading, DateChooser, Textarea, Select, Input, SearchableSelect, Checkbox, CheckboxGroup, FormControl, Badge, SimpleTable, SimpleModal, EmptyBox, ConfirmButton, Tooltip, Wrap, WrapItem, FileChooser } from '@/components/lib'
import api from '../../../api'
import utils, { DAYS, ALLOWED_MESSAGE_FILE_TYPES } from '../../../utils/utils'
import useStore from '../../../store'

// An attachment is stored as the upload's stored name: the original file name
// with the upload's timestamp appended. Messages created before March 2026 hold
// the whole S3 key instead, so drop any path in front of it too.
const attachmentDisplayName = storedName =>
  storedName.split('/').pop().replace(/_\d{9,}(\.[^.]*)?$/, '$1')

const VIA = [
  { name: 'push', label: 'Mobile push' },
  { name: 'email', label: 'Email' },
  { name: 'sms', label: 'SMS' },
  { name: 'whatsapp', label: 'WhatsApp' }
]

export default function AutomaticMessages () {
  const [loading, setLoading] = useState(false)
  const [wizardIsOpen, setWizardIsOpen] = useState(false)
  // 'create' starts from scratch, 'edit' saves back over wizardMessage, and
  // 'copy' seeds a brand new message from it.
  const [wizardMode, setWizardMode] = useState('create')
  const [wizardMessage, setWizardMessage] = useState(null)
  const [automaticMessages, setAutomaticMessages] = useState([])
  const { trial, stages, groups, anchors, setAnchors } = useStore()

  const trialId = trial?._id

  useEffect(() => {
    setLoading(true)
    api.trials.getAutomaticMessages(trialId, data => {
      setLoading(false)
      setAutomaticMessages(data.automaticMessages || [])
    })
    // Load anchors so anchor-relative messages display their anchor name in the
    // list (the wizard also loads these, but the list renders before it opens).
    if (trialId && !anchors?.length) {
      api.anchors.list(trialId, d => setAnchors(d.anchors || []))
    }
  }, [trialId])

  const openWizard = (mode, message = null) => {
    setWizardMode(mode)
    setWizardMessage(message)
    setWizardIsOpen(true)
  }

  const deleteAutomaticMessage = (messageId) => {
    api.trials.deleteAutomaticMessage(trialId, messageId, () => {
      const newAutomaticMessages = Object.assign([], automaticMessages)
      const index = newAutomaticMessages.map(a => a._id).indexOf(messageId)
      newAutomaticMessages.splice(index, 1)
      setAutomaticMessages(newAutomaticMessages)
      utils.toast('success', 'Automatic message has been deleted')
    }, err => {
      utils.toast('error', err.message)
    })
  }

  return (
    <Stack gap={5}>
      <ResponsiveStack justifyContent='space-between'>
        <Box>
          <Heading as='h2' size='lg'>Automatic messages</Heading>
          <Text>Set-up automatic messages that are sent to participants on a schedule or as they interact with your trial.</Text>
        </Box>
        <Button colorPalette='primary' onClick={e => openWizard('create')}><Icon as={FaPlus} mr={2} /> Create a new automatic message</Button>
      </ResponsiveStack>

      <MessageWizard
        isOpen={wizardIsOpen} trialId={trialId} groups={groups} stages={stages}
        mode={wizardMode} message={wizardMessage}
        onCreated={newAutomaticMessage => {
          const newAutomaticMessages = Object.assign([], automaticMessages)
          newAutomaticMessages.splice(0, 0, newAutomaticMessage)
          setAutomaticMessages(newAutomaticMessages)
        }}
        onUpdated={updatedAutomaticMessage => {
          setAutomaticMessages(automaticMessages.map(m => m._id === updatedAutomaticMessage._id ? updatedAutomaticMessage : m))
        }}
        onClose={e => setWizardIsOpen(false)}
      />

      {loading && <Loading />}
      {!loading && !automaticMessages?.length &&
        <EmptyBox
          icon='robot'
          title='Your automatic messages will appear here once created'
          description='Automatic messages enable you to provide automatic support and prompts to your participants as they engage with your trial.'
        />}
      {automaticMessages?.length > 0 &&
        <SimpleTable
          headers={['Send when', 'Via', 'Stage', 'Message', '']}
          rows={automaticMessages.map(message => {
            const stageRow = stages.find(s => s._id === message.stage)
            let stageLabel = stageRow?.name || 'N/A'
            if (stageRow?.isRecurring && message.sendWhen === 'completedStage') {
              if (message.stageRecurrence != null) {
                stageLabel = `${stageLabel} · ${utils.recurrenceLabel(stageRow, message.stageRecurrence)}`
              } else {
                stageLabel = `${stageLabel} · All recurrences`
              }
            }
            return [
              <Box key={message._id}>{message.sendWhen}
                {message.sendWhen === 'schedule' && message.schedule &&
                  <small><br />{utils.getAutomaticMessageScheduleAsString(message, groups)}</small>}
                {message.sendWhen === 'anchor' && message.schedule &&
                  <small><br />{utils.getAutomaticMessageScheduleAsString(message, groups, anchors)}</small>}
              </Box>,
              <Wrap gap={2} key={message._id}>
                {message.sendVia?.map(via =>
                  <WrapItem key={via}><Badge size='xs'>{VIA.find(v => v.name === via)?.label}</Badge></WrapItem>
                )}
                {!message.sendVia?.length && <Badge>Push notification</Badge>}
              </Wrap>,
              <Box key={message._id}>{stageLabel}</Box>,
              <Text key={message._id} fontSize='sm'>{message.message}</Text>,
              <Stack direction='row' justifyContent='end' key={message._id}>
                <Tooltip content='Edit this message'>
                  <Button size='xs' variant='ghost' onClick={e => openWizard('edit', message)}><Icon><FaPencilAlt /></Icon></Button>
                </Tooltip>
                <Tooltip content='Make a copy'>
                  <Button size='xs' variant='ghost' onClick={e => openWizard('copy', message)}><Icon><FaRegCopy /></Icon></Button>
                </Tooltip>
                <ConfirmButton
                  header='Really delete this automatic message?'
                  body={'It can\'t be recovered but you can make a new one instead'}
                  trigger={<Button size='xs' variant='ghost'><Icon><FaTrash /></Icon></Button>}
                  onConfirm={e => deleteAutomaticMessage(message._id)}
                />
              </Stack>
            ]
          })}
        />}

    </Stack>
  )
}

export function MessageWizard ({ isOpen, onClose, trialId, participant, groups, stages, message, mode = 'create', onCreated, onUpdated }) {
  const [newSendWhen, setNewSendWhen] = useState('schedule')
  const [newSendSchedule, setNewSendSchedule] = useState({})
  const [newMessage, setNewMessage] = useState('')
  const [sendVia, setSendVia] = useState(['push'])
  const [attachments, setAttachments] = useState([])
  const [completedStageRecurrence, setCompletedStageRecurrence] = useState('')
  const { anchors: storeAnchors, setAnchors } = useStore()
  // Track whether the anchors fetch has resolved, so we don't flash a
  // "No anchors defined" warning during the initial async load.
  const [anchorsLoaded, setAnchorsLoaded] = useState((storeAnchors?.length || 0) > 0)

  // Ensure anchors are loaded for the anchor picker (AdminDashboard already
  // loads them, but this page may be reachable without that effect having run)
  useEffect(() => {
    if (participant) {
      setNewSendSchedule({ ...newSendSchedule, participant: participant._id })
    }
    if (trialId && !storeAnchors?.length) {
      api.anchors.list(trialId, d => {
        setAnchors(d.anchors || [])
        setAnchorsLoaded(true)
      })
    } else if (storeAnchors?.length) {
      setAnchorsLoaded(true)
    }
  }, [participant, trialId])

  // Fill the form in whenever it opens: from the message being edited or
  // copied, or back to a blank message if there isn't one (so a copy left over
  // from last time doesn't reappear under "create").
  useEffect(() => {
    if (!isOpen) return
    if (!message) {
      setNewSendWhen('schedule')
      setNewSendSchedule(participant ? { participant: participant._id } : {})
      setNewMessage('')
      setSendVia(['push'])
      setAttachments([])
      setCompletedStageRecurrence('')
      return
    }
    setNewSendWhen(message.sendWhen === 'completedStage' && message.stage
      ? `completedStage.${message.stage}`
      : message.sendWhen)
    const schedule = { ...(message.schedule || {}) }
    // The API sends dates as ISO strings; the date pickers work in Date objects.
    if (schedule.dates?.length) schedule.dates = schedule.dates.map(d => new Date(d))
    setNewSendSchedule(schedule)
    setNewMessage(message.message || '')
    setSendVia(message.sendVia?.length ? message.sendVia : ['push'])
    // Only the stored name comes back with the message, so show that.
    setAttachments((message.attachments || []).map(storedName => ({ storedName, name: attachmentDisplayName(storedName) })))
    setCompletedStageRecurrence(message.stageRecurrence != null ? String(message.stageRecurrence) : '')
  }, [isOpen, message?._id, mode])

  const isEditing = mode === 'edit' && message?._id

  const saveAutomaticMessage = () => {
    let sendWhen = newSendWhen
    let stage
    if (sendWhen.indexOf('completedStage.') === 0) {
      stage = sendWhen.replace('completedStage.', '')
      sendWhen = 'completedStage'
    }
    let schedule = Object.assign({}, newSendSchedule)
    if (sendWhen === 'anchor') {
      // Build a clean anchor schedule: drop any daily/weekly-specific fields and
      // replace frequency-driven schedule with anchor / dayOffset / hour only.
      const group = schedule.group || null
      const participantId = schedule.participant || null
      const parsedHour = parseInt(schedule.hour, 10)
      schedule = {
        anchor: schedule.anchor,
        dayOffset: parseInt(schedule.dayOffset, 10) || 0,
        // Default to the displayed 09:00 (not midnight) if somehow unset.
        hour: Number.isInteger(parsedHour) ? parsedHour : 9
      }
      if (group) schedule.group = group
      if (participantId) schedule.participant = participantId
    }
    const payload = { sendWhen, schedule, sendVia, stage, message: newMessage }
    if (sendWhen === 'completedStage' && stage && completedStageRecurrence !== '') {
      payload.stageRecurrence = parseInt(completedStageRecurrence, 10)
    }
    if (attachments.length) payload.attachments = attachments.map(a => a.storedName)
    const fail = err => utils.toast('error', err.message)
    if (isEditing) {
      return api.trials.updateAutomaticMessage(trialId, message._id, payload, automaticMessage => {
        if (onUpdated) onUpdated(automaticMessage)
        onClose()
        utils.toast('success', 'Automatic message updated successfully')
      }, fail)
    }
    api.trials.createAutomaticMessage(trialId, payload, automaticMessage => {
      onCreated(automaticMessage)
      onClose()
      setAttachments([])
      setCompletedStageRecurrence('')
      utils.toast('success', 'Automatic message created successfully')
    }, fail)
  }

  return (
    <SimpleModal
      isOpen={isOpen} onClose={onClose} modalProps={{ size: '3xl' }}
      header={`${isEditing ? 'Edit this automatic scheduled message' : mode === 'copy' ? 'Make a copy of this automatic scheduled message' : 'Create a new automatic scheduled message'} ${participant ? `for ${participant.id}` : ''}`}
      body={
        <Stack gap={5}>
          {participant &&
            <FormControl label='Send to'>
              <Text>Participant <Badge>{participant.id}</Badge></Text>
            </FormControl>}

          <FormControl label='Message body' helperText={'Tip: Use {PARTICIPANT_ID} as a placeholder that will be replaced with the recipient participant\'s ID.'}>
            <Textarea id='message' placeholder='Write your message...' value={newMessage} onChange={e => setNewMessage(e.target.value)} />
          </FormControl>

          <FormControl label='Attachments' helperText='PDF, images (JPG, PNG, GIF, WebP), or DOCX. Max 10MB per file.'>
            <Stack gap={2}>
              {attachments.length > 0 &&
                <Stack direction='row' flexWrap='wrap' gap={2}>
                  {attachments.map((a, i) => (
                    <Box key={i} display='flex' alignItems='center' gap={1} px={2} py={1} bg='bg.subtle' rounded='md' fontSize='xs'>
                      <Icon as={FaPaperclip} />
                      <Text fontSize='xs'>{a.name}</Text>
                      <Icon as={FaTimes} cursor='pointer' onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))} />
                    </Box>
                  ))}
                </Stack>}
              <FileChooser
                forType='trial'
                forObject={{ _id: trialId }}
                accept={ALLOWED_MESSAGE_FILE_TYPES}
                multiple
                onComplete={file => setAttachments(prev => [...prev, { storedName: file.storedName, name: file.name }])}
                onError={err => utils.toast('error', `Upload failed: ${err}`)}
                trigger={<Button size='sm' variant='outline'><Icon as={FaPaperclip} mr={1} /> Add attachment</Button>}
              />
            </Stack>
          </FormControl>

          <FormControl label='What triggers this message'>
            <Select
              id='sendWhen' value={newSendWhen} onChange={e => {
                setNewSendWhen(e.target.value)
                setCompletedStageRecurrence('')
                // Seed sensible anchor defaults so the displayed dayOffset/hour
                // match what actually gets submitted (the selects show these
                // values by default but wouldn't otherwise be in state until
                // the user interacts with them).
                if (e.target.value === 'anchor') {
                  setNewSendSchedule(s => ({
                    ...s,
                    dayOffset: s?.dayOffset ?? 1,
                    hour: s?.hour ?? 9
                  }))
                }
              }}
            >
              <option>Select one</option>
              <option value='schedule'>Send according to a schedule</option>
              <option value='anchor'>Send relative to a participant's anchor date</option>
              {/* Joining the trial and completing a stage fire for whoever
                  triggered them, so they can't be aimed at one participant. */}
              {!participant && <option value='joinedTrial'>Send when the participant joins the trial</option>}
              {!participant && stages?.map(stage =>
                <option key={stage._id} value={`completedStage.${stage._id}`}>Send when the participant completes stage {stage.name}</option>
              )}
            </Select>
          </FormControl>

          {newSendWhen?.startsWith('completedStage.') && (() => {
            const stageId = newSendWhen.replace('completedStage.', '')
            const sel = stages?.find(s => s._id === stageId)
            return sel?.isRecurring && (sel.recurringDuration > 0)
              ? (
                <FormControl label='Stage recurrence' helperText='Limit this message to a single pass through the stage, or leave as all recurrences.'>
                  <Select
                    value={completedStageRecurrence}
                    onChange={e => setCompletedStageRecurrence(e.target.value)}
                  >
                    <option value=''>All recurrences</option>
                    {Array.from({ length: sel.recurringDuration }, (_, i) => i).map(recurrence =>
                      <option key={recurrence} value={String(recurrence)}>{utils.recurrenceLabel(sel, recurrence)}</option>
                    )}
                  </Select>
                </FormControl>
                )
              : null
          })()}

          {newSendWhen === 'schedule' &&
            <>
              <FormControl p={3} rounded='md' bg='bg.subtle' label='Configure your send schedule'>
                {!participant &&
                  <Select value={newSendSchedule?.group} onChange={e => setNewSendSchedule({ ...newSendSchedule, group: e.target.value || null })}>
                    <option value=''>Send to all participants</option>
                    {groups?.map(g =>
                      <option key={g._id} value={g._id}>Send to participants in {g.name}</option>
                    )}
                  </Select>}

                <Select value={newSendSchedule?.frequency} onChange={e => setNewSendSchedule({ ...newSendSchedule, frequency: e.target.value })}>
                  <option>Select a schedule...</option>
                  <option value='daily'>Send every day (participant's local time)</option>
                  <option value='weekly'>Send once per week (participant's local time)</option>
                  <option value='specific'>Send at specific dates and times</option>
                </Select>
                {newSendSchedule?.frequency === 'weekly' &&
                  <Select value={newSendSchedule?.day} onChange={e => setNewSendSchedule({ ...newSendSchedule, day: parseInt(e.target.value) })}>
                    <option>On...</option>
                    {Array.from({ length: 7 }, (_, i) => i).map(dayIndex =>
                      <option key={dayIndex} value={dayIndex}>On {DAYS[dayIndex]}</option>
                    )}
                  </Select>}
                {['daily', 'weekly'].includes(newSendSchedule?.frequency) &&
                  <Select value={newSendSchedule?.hour} onChange={e => setNewSendSchedule({ ...newSendSchedule, hour: parseInt(e.target.value) })}>
                    <option>At...</option>
                    {Array.from({ length: 24 }, (_, i) => i).map(hour =>
                      <option key={hour} value={hour}>At {hour < 10 && '0'}{hour}:00</option>
                    )}
                  </Select>}
                {newSendSchedule?.frequency === 'specific' &&
                  <Stack>
                    <Text fontWeight='semibold' fontSize='sm'>Send at the following times:</Text>
                    {!newSendSchedule?.dates?.length &&
                      <Text fontStyle='italic' fontSize='sm' color='orange.600'>No dates and times selected yet</Text>}
                    {newSendSchedule?.dates?.map((date, i) =>
                      <Stack direction='row' key={date}>
                        <DateChooser
                          withTime value={date} onChange={d => {
                            const newDates = Object.assign([], newSendSchedule.dates)
                            const sameDate = newDates.find(d2 => d.toISOString() === d2.toISOString())
                            if (sameDate) return utils.toast('error', 'This date is already selected') // Can't have two dates the same
                            newDates[i] = d
                            setNewSendSchedule({ ...newSendSchedule, dates: newDates })
                          }}
                        />
                        <Button onClick={e => setNewSendSchedule({ ...newSendSchedule, dates: newSendSchedule.dates.filter((_, j) => j !== i) })}>Remove</Button>
                      </Stack>)}
                    <Button
                      colorPalette='blue' variant='outline' size='sm' onClick={e => {
                      // The code below ensures the date we're adding is one hour after the current latest one
                      // This also helps ensure uniqueness in the list (so we don't mess up React keys)
                        const currentDates = Object.assign([], newSendSchedule?.dates || [])
                        const currentDatesSorted = currentDates.sort((a, b) => b - a) // latest date first
                        const latestDate = currentDatesSorted[0] || new Date() // "||"" in case this is first time adding
                        const newDate = new Date(latestDate)
                        newDate.setHours(newDate.getHours() + 1)
                        newDate.setMinutes(0, 0, 0)
                        setNewSendSchedule({ ...newSendSchedule, dates: [...newSendSchedule.dates || [], newDate] })
                      }}
                    ><Icon as={FaPlus} /> Add a date and time
                    </Button>
                  </Stack>}
              </FormControl>
            </>}

          {newSendWhen === 'anchor' &&
            <>
              <FormControl
                p={3} rounded='md' bg='bg.subtle'
                label='Send relative to an anchor date'
                helperText='Each participant receives this once, at the chosen time-of-day on the day relative to their own anchor date.'
              >
                {!participant &&
                  <Select value={newSendSchedule?.group} onChange={e => setNewSendSchedule({ ...newSendSchedule, group: e.target.value || null })}>
                    <option value=''>Send to all participants</option>
                    {groups?.map(g =>
                      <option key={g._id} value={g._id}>Send to participants in {g.name}</option>
                    )}
                  </Select>}

                {!anchorsLoaded && <Loading />}
                {anchorsLoaded && storeAnchors?.length === 0 &&
                  <Alert status='warning' fontSize='sm'>No anchors defined. Add anchors on the Anchors tab first.</Alert>}
                {anchorsLoaded && storeAnchors?.length > 0 &&
                  <Stack>
                    <Text fontSize='sm'>Anchor date:</Text>
                    <SearchableSelect
                      value={newSendSchedule?.anchor || ''}
                      onChange={selected => setNewSendSchedule({ ...newSendSchedule, anchor: selected })}
                      placeholder='Select an anchor'
                      options={(storeAnchors || []).map(a => ({ value: a._id, label: a.name }))}
                    />
                  </Stack>}

                <Stack direction='row' alignItems='center' flexWrap='wrap'>
                  <Select
                    value={(newSendSchedule?.dayOffset ?? 1) >= 0 ? 'after' : 'before'}
                    onChange={e => {
                      const goingBefore = e.target.value === 'before'
                      const magnitude = Math.max(1, Math.abs(parseInt(newSendSchedule?.dayOffset, 10) || 1))
                      setNewSendSchedule({ ...newSendSchedule, dayOffset: goingBefore ? -magnitude : magnitude })
                    }}
                  >
                    <option value='after'>After</option>
                    <option value='before'>Before</option>
                  </Select>
                  <Input
                    type='number' min='0' w='80px' bg='bg.surface'
                    value={Math.abs(parseInt(newSendSchedule?.dayOffset, 10) || 0)}
                    onChange={e => {
                      const magnitude = Math.max(0, parseInt(e.target.value, 10) || 0)
                      const negative = (newSendSchedule?.dayOffset ?? 1) < 0
                      setNewSendSchedule({ ...newSendSchedule, dayOffset: negative ? -magnitude : magnitude })
                    }}
                  />
                  <Text fontSize='sm'>days the anchor date, at</Text>
                  <Select
                    value={newSendSchedule?.hour ?? 9}
                    onChange={e => setNewSendSchedule({ ...newSendSchedule, hour: parseInt(e.target.value, 10) })}
                  >
                    {Array.from({ length: 24 }, (_, i) => i).map(hour =>
                      <option key={hour} value={hour}>{hour < 10 && '0'}{hour}:00</option>
                    )}
                  </Select>
                  <Text fontSize='sm' color='fg.muted'>(participant's local time)</Text>
                </Stack>
              </FormControl>
              {isEditing &&
                <Alert size='sm' status='warning'>
                  Participants who have already been sent this message won't be sent it again, whatever you change here. To send an amended version to everyone, make a copy of this message instead.
                </Alert>}
            </>}

          <Box>
            <Stack gap={3}>
              <Heading as='h3' size='sm'>Send via</Heading>
              <CheckboxGroup value={sendVia} onValueChange={setSendVia} name='via'>
                <Stack direction='row'>
                  {VIA.map(via =>
                    <Checkbox key={via.name} value={via.name}>{via.label}</Checkbox>
                  )}
                </Stack>
              </CheckboxGroup>

              {sendVia.includes('push') &&
                <Alert size='sm' status='info'>
                  Sending via push requires participants to have the mobile app installed and to have agreed or enabled push notifications.
                </Alert>}
              {(sendVia.includes('email') || sendVia.includes('sms') || sendVia.includes('whatsapp')) &&
                <Alert size='sm' status='info'>
                  Sending via email, SMS or WhatsApp requires participants to have contact details set.
                </Alert>}
              {sendVia.includes('sms') &&
                <Alert size='sm' status='warning'>
                  Sending a large number of SMS messages may incur significant charges.
                </Alert>}
              {!sendVia.length &&
                <Alert size='sm' status='warning'>
                  You must select at least one method of sending the message.
                </Alert>}
            </Stack>
          </Box>
        </Stack>
}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button colorPalette='primary' onClick={saveAutomaticMessage}>{isEditing ? 'Save changes' : mode === 'copy' ? 'Create copy' : 'Complete setup'}</Button>
        </>
}
    />
  )
}
